import { useMemo } from "react";
import {
  DataTable,
  initialsOf,
  OwnerChip,
  StatusDot,
  StatusPill,
  type DataTableColumn,
} from "@playbookai/ui";
import type { ManagedKpiObservation, ManagedKpiDetail } from "../api/types.js";
import { useOwnerNames } from "../hooks/useOwnerNames.js";
import { formatDateTime } from "./kpi-presentation.js";

export interface KpiValueHistoryTableProps {
  readonly kpi: ManagedKpiDetail;
  /** The loaded range, ASCENDING by `observedAt` — the order
   * `useKpiObservations` normalises to for the chart. */
  readonly observations: readonly ManagedKpiObservation[];
}

/**
 * How many readings the table renders at once.
 *
 * The hook behind it sweeps up to 2,000 rows so the chart can draw a complete
 * series, and a table of 2,000 rows is a different thing from a chart of 2,000
 * points — it is unreadable and slow. The cap is STATED under the table
 * whenever it bites, because a silently truncated history reads as a complete
 * one; narrowing the range is how a reader reaches the rest.
 */
const HISTORY_ROW_LIMIT = 50;

/**
 * Value history (PB-2941) — the readings behind the chart, as rows.
 *
 * WHY A TABLE BESIDE THE CHART. The chart answers "which way is this going";
 * the table answers "what exactly was reported, when, by whom" — which is the
 * question an append-only ledger exists to answer, and the one a trend line
 * cannot. It is the visible form of "a correction is another observation": a
 * superseded reading stays in the list rather than disappearing behind the value
 * that replaced it.
 *
 * EVERY NUMBER RENDERS FROM THE WIRE STRING. `parseObservationValue` exists for
 * the chart's geometry alone; a `DECIMAL(18,4)` beyond `Number.MAX_SAFE_INTEGER`
 * loses precision through a float, which is invisible at chart resolution and
 * wrong in a table cell.
 *
 * THE REPORTING PERIOD IS DERIVED PER ROW, from that row's own `observedAt`.
 * Nothing stores a period, so each reading names the period it falls in rather
 * than one it was filed against — the honest reading of the current data model.
 */
export function KpiValueHistoryTable({
  kpi,
  observations,
}: KpiValueHistoryTableProps): JSX.Element {
  // Newest first: a reader scanning a ledger wants the latest reading at the
  // top, which is also the server's own order (the ascending form exists for
  // the chart). Sliced AFTER reversing, so the cap keeps the most recent rows.
  const rows = useMemo(
    () => [...observations].reverse().slice(0, HISTORY_ROW_LIMIT),
    [observations],
  );

  const recorderIds = useMemo(
    () => rows.map((row) => row.recordedByUserId),
    [rows],
  );
  // The same host `PeopleSource` the Owner chips use, so a name resolved
  // once for the KPI's owner is not fetched again for their readings.
  const recorderNames = useOwnerNames(recorderIds).data;

  /**
   * Which row the KPI's CURRENT value comes from.
   *
   * Derived by matching the server's projection (`currentState.observationTime`,
   * which it computes as the newest row by `observed_at`) rather than by taking
   * the first row of this table. Two things make that necessary: a narrowed
   * range may not contain the current reading at all, and a BACKDATED entry is
   * the newest row INSERTED without being the newest row MEASURED — marking the
   * last insert as current is exactly the bug the server's recompute avoids.
   *
   * On an instant shared by several readings, the greatest `observationId` wins,
   * which is the server's own `(observed_at, observation_id) DESC` tiebreak —
   * without it a tie would mark two rows current and one of them would be wrong.
   */

  /**
   * Which row the KPI's CURRENT value comes from — matched against the
   * server's projection (`currentState.observationTime`, the newest row by
   * `observed_at`), never the first row of this table: a narrowed range may
   * not contain the current reading, and a BACKDATED entry is the newest row
   * inserted without being the newest measured. On a shared instant the
   * greatest `observationId` wins, the server's own tiebreak.
   */
  const currentObservationId = useMemo(() => {
    const at = kpi.currentState?.observationTime;
    if (!at) return null;
    return rows
      .filter((row) => row.observedAt === at)
      .reduce<
        string | null
      >((best, row) => (best === null || row.observationId > best ? row.observationId : best), null);
  }, [rows, kpi.currentState?.observationTime]);

  const columns = useMemo<
    ReadonlyArray<DataTableColumn<ManagedKpiObservation>>
  >(
    () => [
      {
        id: "value",
        header: "Value",
        cardRole: "title",
        cell: (row) => (
          <>
            <span className="font-medium tabular-nums text-pb-text">
              {row.value}
            </span>
            {kpi.unit ? (
              <span className="ms-1 text-pb-text-muted">{kpi.unit}</span>
            ) : null}
            {row.observationId === currentObservationId ? (
              <StatusPill tone="neutral" className="ms-2">
                Current
              </StatusPill>
            ) : null}
          </>
        ),
      },
      {
        id: "observedAt",
        header: "Observed",
        sortable: true,
        sortAccessor: (row) => row.observedAt,
        cell: (row) => (
          <span className="text-pb-text-secondary">
            {formatDateTime(row.observedAt)}
          </span>
        ),
      },
      {
        id: "source",
        header: "Source",
        cell: (row) => (
          <span className="inline-flex items-center gap-1.5 text-pb-text-secondary">
            <StatusDot
              size="xs"
              tint={row.source === "automated" ? 4 : 5}
              aria-hidden="true"
            />
            {row.source === "automated" ? "Automated" : "Manual"}
          </span>
        ),
      },
      {
        id: "recordedBy",
        header: "Recorded by",
        cell: (row) => {
          // A null recorder is the deferred automated writer, which has no
          // human behind it — saying so is truthful where a dash would look
          // like a lookup that failed.
          if (!row.recordedByUserId) {
            return (
              <span className="text-pb-text-muted">
                {row.source === "automated" ? "Automated" : "—"}
              </span>
            );
          }
          const name = recorderNames?.[row.recordedByUserId]?.name;
          return name ? (
            <OwnerChip initials={initialsOf(name)} name={name} />
          ) : (
            <span className="text-pb-text-muted">Recorder assigned</span>
          );
        },
      },
      {
        id: "note",
        header: "Note",
        cell: (row) =>
          row.note ? (
            <span className="text-pb-text-secondary">{row.note}</span>
          ) : (
            <span className="text-pb-text-muted">—</span>
          ),
      },
    ],
    [kpi.unit, recorderNames, currentObservationId],
  );

  return (
    <div className="space-y-2">
      <h3 className="text-body font-medium text-pb-text">Recorded values</h3>
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => row.observationId}
        density="compact"
        responsive
        caption={`${kpi.name} value history`}
        emptyState={
          <p className="text-pb-text-muted">No readings in this range yet.</p>
        }
      />
      <p className="text-caption text-pb-text-muted">
        Values are append-only — a correction is recorded as a new value, and
        the earlier reading stays visible.
      </p>
      {observations.length > HISTORY_ROW_LIMIT ? (
        <p className="text-dense-1 text-pb-text-muted">
          Showing the {HISTORY_ROW_LIMIT} most recent of {observations.length}{" "}
          readings in this range. Narrow the range to see earlier ones.
        </p>
      ) : null}
    </div>
  );
}
